<template>
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs :breadcrumbs="[
          { name: 'Dashboard', href: route('dashboard') },
          { name: 'Berita', href: route('berita.index') },
        ]" />

        <!-- Header Section -->
        <div class="mb-4 sm:mb-6">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Daftar Berita</h3>
              <p class="mt-1 text-sm text-gray-600">
                Kelola berita dan artikel sistem
              </p>
            </div>
            <div class="mt-4 sm:mt-0">
              <Link v-if="$page.props.auth.user && $page.props.can.berita_create" :href="route('berita.create')"
                class="inline-flex items-center px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200">
              <i class="mr-2 fa-solid fa-plus"></i>
              Tambah Berita
              </Link>
            </div>
          </div>
        </div>

        <!-- Search Input -->
        <div class="relative mb-4">
          <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <MagnifyingGlassVue class="w-4 h-4 text-gray-400" />
          </div>
          <input v-model="search" type="text" autocomplete="off" placeholder="Cari berita..." id="search"
            class="block w-full px-3 py-2.5 pl-10 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200" />
        </div>

        <!-- List -->
        <div class="overflow-hidden bg-white border border-gray-200 shadow-sm rounded-xl">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th scope="col"
                    class="py-3.5 pl-4 pr-3 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase sm:pl-6 w-12">
                    #
                  </th>
                  <th scope="col"
                    class="px-3 py-3.5 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase min-w-[260px]">
                    Berita
                  </th>
                  <th scope="col"
                    class="px-3 py-3.5 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase hidden lg:table-cell min-w-[140px]">
                    Tags
                  </th>
                  <th scope="col"
                    class="px-3 py-3.5 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase hidden md:table-cell whitespace-nowrap">
                    Tanggal
                  </th>
                  <th scope="col"
                    class="px-3 py-3.5 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase whitespace-nowrap">
                    Status
                  </th>
                  <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span class="sr-only">Aksi</span>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-100">
                <tr v-for="(newsItem, index) in news.data" :key="newsItem.id"
                  class="transition-colors duration-150 hover:bg-gray-50">
                  <td class="py-4 pl-4 pr-3 text-sm text-gray-400 whitespace-nowrap sm:pl-6">
                    {{ getRowNumber(index) }}
                  </td>

                  <!-- Berita: thumbnail (klik untuk galeri) + judul + ringkasan + meta mobile -->
                  <td class="px-3 py-4">
                    <div class="flex items-start gap-3">
                      <button type="button" @click="openImageModal(newsItem)"
                        class="relative shrink-0 focus:outline-none"
                        :class="imageCount(newsItem.image) ? 'cursor-pointer' : 'cursor-default'"
                        :title="imageCount(newsItem.image) ? 'Lihat gambar' : ''">
                        <img v-if="firstImage(newsItem.image)" :src="firstImage(newsItem.image)" alt="Gambar berita"
                          class="object-cover transition-opacity rounded-lg w-14 h-14 ring-1 ring-gray-200 hover:opacity-80" />
                        <div v-else
                          class="flex items-center justify-center text-gray-400 bg-gray-100 rounded-lg w-14 h-14">
                          <i class="fa-regular fa-image"></i>
                        </div>
                        <span v-if="imageCount(newsItem.image) > 1"
                          class="absolute flex items-center justify-center w-4 h-4 text-[10px] font-medium text-white bg-indigo-600 rounded-full -top-1 -right-1">
                          {{ imageCount(newsItem.image) }}
                        </span>
                      </button>

                      <div class="min-w-0">
                        <div class="font-semibold text-gray-900 line-clamp-2">
                          {{ newsItem.title }}
                        </div>
                        <p class="mt-0.5 text-xs text-gray-500 line-clamp-1">
                          {{ excerpt(newsItem.content) }}
                        </p>

                        <!-- Meta mobile -->
                        <div class="flex flex-wrap items-center gap-2 mt-2 md:hidden">
                          <span :class="isPublished(newsItem.published)
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-700'"
                            class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full">
                            {{ isPublished(newsItem.published) ? "Publish" : "Draft" }}
                          </span>
                          <span class="text-xs text-gray-400">{{ newsItem.publish_date }}</span>
                          <span class="inline-flex items-center gap-1 text-xs text-gray-400">
                            <i class="fa-regular fa-eye"></i>
                            {{ newsItem.formatted_view_count ?? newsItem.view_count ?? 0 }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>

                  <!-- Tags -->
                  <td class="hidden px-3 py-4 align-top lg:table-cell">
                    <div v-if="newsItem.tags && newsItem.tags.length" class="flex flex-wrap gap-1">
                      <span v-for="tag in newsItem.tags.slice(0, 2)" :key="tag"
                        class="inline-flex items-center px-2 py-0.5 text-xs font-medium text-indigo-700 rounded-full bg-indigo-50">
                        {{ tag }}
                      </span>
                      <span v-if="newsItem.tags.length > 2"
                        class="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-full">
                        +{{ newsItem.tags.length - 2 }}
                      </span>
                    </div>
                    <span v-else class="text-xs text-gray-400">—</span>
                  </td>

                  <!-- Tanggal -->
                  <td class="hidden px-3 py-4 text-sm text-gray-600 align-top md:table-cell whitespace-nowrap">
                    {{ newsItem.publish_date }}
                  </td>

                  <!-- Status -->
                  <td class="px-3 py-4 align-top">
                    <span :class="isPublished(newsItem.published)
                      ? 'bg-green-100 text-green-700'
                      : 'bg-amber-100 text-amber-700'"
                      class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap">
                      <span :class="isPublished(newsItem.published) ? 'bg-green-500' : 'bg-amber-500'"
                        class="w-1.5 h-1.5 rounded-full"></span>
                      {{ isPublished(newsItem.published) ? "Publish" : "Draft" }}
                    </span>
                  </td>

                  <!-- Aksi -->
                  <td class="py-4 pl-3 pr-4 text-right align-top sm:pr-6 whitespace-nowrap">
                    <div class="flex items-center justify-end gap-2">
                      <Link v-if="$page.props.can.berita_show" :href="route('berita.show', newsItem.idnews)"
                        class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 transition-colors duration-150 rounded-md bg-indigo-50 hover:bg-indigo-100 sm:px-2.5 sm:py-1.5"
                        title="Lihat Detail">
                      <i class="w-3 h-3 fa-solid fa-eye"></i>
                      <span class="hidden ml-1 sm:inline">Lihat</span>
                      </Link>
                      <button v-if="$page.props.can.berita_delete" @click="deleteNews(newsItem.idnews)"
                        class="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 transition-colors duration-150 rounded-md bg-red-50 hover:bg-red-100 sm:px-2.5 sm:py-1.5"
                        title="Hapus Berita">
                        <i class="w-3 h-3 fa-solid fa-trash"></i>
                        <span class="hidden ml-1 sm:inline">Hapus</span>
                      </button>
                    </div>
                  </td>
                </tr>

                <!-- Empty state -->
                <tr v-if="!news.data || news.data.length === 0">
                  <td colspan="6" class="px-3 py-16 text-center">
                    <i class="mb-3 text-3xl text-gray-300 fa-regular fa-newspaper"></i>
                    <p class="text-sm font-medium text-gray-900">Belum ada berita</p>
                    <p class="mt-1 text-sm text-gray-500">
                      {{ search
                        ? "Tidak ada berita yang cocok dengan pencarian."
                        : "Mulai dengan menambahkan berita baru." }}
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination -->
        <Pagination :data="news" :updatedPageNumber="updatedPageNumber" />
      </div>

      <!-- Image Gallery Modal -->
      <div v-if="imageModal.show" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
        @click="closeImageModal">
        <div class="relative max-w-4xl mx-4 overflow-hidden bg-white rounded-lg" @click.stop>
          <!-- Modal Header -->
          <div class="flex items-center justify-between p-4 border-b bg-gray-50">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">{{ imageModal.title }}</h3>
              <p class="text-sm text-gray-600">{{ imageModal.images.length }} gambar</p>
            </div>
            <button @click="closeImageModal" class="text-gray-400 transition-colors hover:text-gray-600">
              <i class="text-xl fa-solid fa-times"></i>
            </button>
          </div>

          <!-- Image Display -->
          <div class="p-4">
            <div class="mb-4">
              <img :src="imageModal.images[imageModal.currentIndex]" :alt="`Gambar ${imageModal.currentIndex + 1}`"
                class="object-contain w-full rounded-lg max-h-96">
            </div>

            <!-- Navigation Buttons (if multiple images) -->
            <div v-if="imageModal.images.length > 1" class="flex items-center justify-between mb-4">
              <button @click="previousImage" :disabled="imageModal.currentIndex === 0"
                class="px-3 py-2 transition-colors bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">
                <i class="mr-1 fa-solid fa-chevron-left"></i>
                Sebelumnya
              </button>
              <span class="text-sm text-gray-600">
                {{ imageModal.currentIndex + 1 }} / {{ imageModal.images.length }}
              </span>
              <button @click="nextImage" :disabled="imageModal.currentIndex === imageModal.images.length - 1"
                class="px-3 py-2 transition-colors bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">
                Selanjutnya
                <i class="ml-1 fa-solid fa-chevron-right"></i>
              </button>
            </div>

            <!-- Thumbnail Strip (if multiple images) -->
            <div v-if="imageModal.images.length > 1" class="flex pb-2 space-x-2 overflow-x-auto">
              <button v-for="(img, index) in imageModal.images" :key="index" @click="imageModal.currentIndex = index"
                :class="[
                  'flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all',
                  imageModal.currentIndex === index ? 'border-indigo-500' : 'border-gray-200 hover:border-gray-300'
                ]">
                <img :src="img" :alt="`Thumbnail ${index + 1}`" class="object-cover w-full h-full">
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>
<script setup>
import { ref, onMounted, onUnmounted, computed, watch, inject } from "vue";
import Container from "@/Layouts/Container.vue";
import Pagination from "@/Components/Pagination.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import Swal from "sweetalert2";
import { router, usePage } from "@inertiajs/vue3";
const route = inject('route');

const props = defineProps({
  news: {
    type: Object,
    required: true,
  },
  allTags: {
    type: Array,
    default: () => [],
  },
});

/**Pagination */
let search = ref(usePage().props.search),
  pageNumber = ref(1);

// Image Modal State
const imageModal = ref({
  show: false,
  images: [],
  currentIndex: 0,
  title: ''
});

let newsUrl = computed(() => {
  let url = new URL(route("berita.index"));
  url.searchParams.append("page", pageNumber.value);
  if (search.value) {
    url.searchParams.append("search", search.value);
  }
  return url;
});

const updatedPageNumber = (link) => {
  pageNumber.value = link.url.split("=")[1];
};

// Strip HTML tags and collapse whitespace to produce a clean text excerpt.
const excerpt = (html, max = 120) => {
  const text = (html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > max ? text.slice(0, max).trim() + "…" : text;
};

// `image` is cast to an array; return the first usable URL (or null).
const firstImage = (image) => {
  if (Array.isArray(image)) return image[0] || null;
  if (typeof image === "string" && image) return image;
  return null;
};

const imageCount = (image) =>
  Array.isArray(image) ? image.length : image ? 1 : 0;

const isPublished = (value) => Number(value) === 1;

// Row number calculation with fallback for pagination
const getRowNumber = (index) => {
  const currentPage = props.news.current_page || 1;
  const perPage = props.news.per_page || 10;
  const calculated = (currentPage - 1) * perPage + index + 1;
  return isNaN(calculated) ? index + 1 : calculated;
};

watch(
  () => newsUrl.value,
  (updateNewsUrl) => {
    router.visit(updateNewsUrl, {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    });
  }
);

watch(
  () => search.value,
  (value) => {
    if (value) {
      pageNumber.value = 1;
    }
  }
);
//End of Pagination

// Delete function with improved SweetAlert2
const deleteNews = async (newsId) => {
  const result = await Swal.fire({
    title: "Apakah Anda yakin?",
    text: "Berita akan dihapus permanen!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, hapus!",
    cancelButtonText: "Batal",
  });

  if (result.isConfirmed) {
    // Show loading
    Swal.fire({
      title: "Menghapus...",
      text: "Sedang menghapus berita",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    router.delete(route("berita.destroy", newsId), {
      onSuccess: () => {
        Swal.fire({
          title: "Terhapus!",
          text: "Berita berhasil dihapus",
          icon: "success",
          confirmButtonText: "OK",
        });
      },
      onError: () => {
        Swal.fire({
          title: "Error!",
          text: "Terjadi kesalahan saat menghapus berita",
          icon: "error",
          confirmButtonText: "OK",
        });
      },
      preserveScroll: true,
    });
  }
};

// Image Modal Functions
const openImageModal = (newsItem) => {
  let images = [];

  if (Array.isArray(newsItem.image)) {
    images = newsItem.image;
  } else if (newsItem.image) {
    images = [newsItem.image];
  }

  if (images.length > 0) {
    imageModal.value = {
      show: true,
      images: images,
      currentIndex: 0,
      title: newsItem.title
    };
  }
};

const closeImageModal = () => {
  imageModal.value = {
    show: false,
    images: [],
    currentIndex: 0,
    title: ''
  };
};

const nextImage = () => {
  if (imageModal.value.currentIndex < imageModal.value.images.length - 1) {
    imageModal.value.currentIndex++;
  }
};

const previousImage = () => {
  if (imageModal.value.currentIndex > 0) {
    imageModal.value.currentIndex--;
  }
};

// Keyboard navigation for modal
const handleKeyDown = (event) => {
  if (!imageModal.value.show) return;

  if (event.key === 'Escape') {
    closeImageModal();
  } else if (event.key === 'ArrowRight') {
    nextImage();
  } else if (event.key === 'ArrowLeft') {
    previousImage();
  }
};

onMounted(() => {
  // Add keyboard event listener
  document.addEventListener('keydown', handleKeyDown);
  const page = usePage();
  if (page.props.flash && page.props.flash.success) {
    Swal.fire({
      title: "Berhasil!",
      text: page.props.flash.success,
      icon: "success",
      confirmButtonText: "OK",
    });
  }
});

// Cleanup event listener
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown);
});
</script>

<style scoped>
/* Ensure proper text truncation */
:deep(.line-clamp-1) {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.line-clamp-2) {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Scrollbar styling for thumbnail strip */
.overflow-x-auto::-webkit-scrollbar {
  height: 4px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 2px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
